import { SubmitContainmentPayload } from "lib/api";
import { ContainmentHoldDTO } from "models/dto/containmentHold";
import { ImpactedPlant, Plant } from "models";
import { UploadContentTypeLPN } from "lib/util/constants";
import { GetContainmentTypeConfig } from "pages/Upload/hooks/containmentTypeConfig";

// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
interface BuildSubmitContainmentPayloadInput {
	file: Opt<File>;
	containmentName: string;
	description: string;
	sectorTypes: any[];
	plants: ImpactedPlant[];
	containmentInitialHold: Opt<ContainmentHoldDTO>;
	containmentSubsequentHold: Opt<ContainmentHoldDTO>;
	partNumber: string;
	duns: Opt<Plant>;
	reason: any;
	uploadContentType: string | null;
}

/**
 * Constructs a containment submission payload from form data for multi-sector containments.
 * Supports both Flexnet (with hold types) and ASRS (without hold types) containments.
 *
 * @throws Error if no impacted sector types are selected
 * @throws Error if file is not provided (via Opt.Expect)
 */
// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
export function buildSubmitContainmentPayload({
	file,
	containmentName,
	description,
	sectorTypes,
	plants,
	containmentInitialHold,
	containmentSubsequentHold,
	partNumber,
	duns,
	reason,
	uploadContentType,
}: BuildSubmitContainmentPayloadInput): SubmitContainmentPayload {
	const impactedSectorTypes = (Array.isArray(sectorTypes)
		? sectorTypes.filter(Boolean)
		: []
	).map((st: any) => {
		const { plants, ...sectorTypeData } = st;
		return sectorTypeData;
	});

	if (impactedSectorTypes.length === 0) {
		throw new Error("No impacted sector types selected");
	}

	const typeName = (impactedSectorTypes[0] as any)?.type?.name;
	const typeConfig = typeName ? GetContainmentTypeConfig(typeName) : undefined;

	const containment: any = {
		name: containmentName,
		description,
		impactedSectorTypes,
		reason,
		impactedPlants: plants.Map((plant) => {
			const { sectorId, _sectorContext, ...plantData } = plant as any;
			return { plant: plantData };
		}),
	};

	if (containmentInitialHold && !containmentInitialHold.IsNone()) {
		containment.initial = containmentInitialHold.Expect("initial");
	}
	if (containmentSubsequentHold && !containmentSubsequentHold.IsNone()) {
		containment.subsequent = containmentSubsequentHold.Expect("subsequent");
	}

	const payload: SubmitContainmentPayload = {
		file: file.Expect("no file selected"),
		containment,
	};

	if (typeConfig?.showUploadContentType && uploadContentType) {
		payload.byLpn = uploadContentType === UploadContentTypeLPN;
	}

	if (partNumber) {
		payload.partNumber = partNumber;
		payload.onePart = true;
	}

	if (duns && !duns.IsNone()) {
		duns.IfSome((supplierPlant) => {
			payload.supplierPlant = supplierPlant;
		});
	}

	return payload;
}
